<template>
  <div id="wrapper" data-bs-theme="dark">
    <a href="#main-content" class="skip-link">Skip to main content</a>

    <!-- Sidebar Layout with all slots -->
    <SidebarLayout>
      <!-- Branding slot -->
      <template #branding>
        <Branding />
      </template>

      <!-- Tabs slot - Empty since navigation is in Branding -->
      <template #tabs>
        <!-- Navigation moved to Branding.vue (left-nav area) -->
      </template>

      <!-- Tab content slot -->
      <template #tab-content>
        <InvestigationsTabContent />
      </template>

      <!-- Toolbar slot -->
      <template #toolbar>
        <ToolShortcuts />
      </template>
    </SidebarLayout>

    <!-- Main content area with router-view -->
    <main
      id="main-content"
      role="main"
      tabindex="-1"
      class="content-area"
      :class="{ 'expanded': sidebarHidden }"
    >
      <!-- Route transition with fade effect (200ms) -->
      <transition name="fade" mode="out-in">
        <router-view v-slot="{ Component }">
          <component :is="Component" />
        </router-view>
      </transition>
    </main>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount } from 'vue';
import { useSidebar } from './composables/useSidebar.js';
import { useAnalytics } from './composables/useAnalytics.js';

// Sidebar components
import SidebarLayout from './components/frame/SidebarLayout.vue';
import Branding from './components/frame/Branding.vue';
import ToolShortcuts from './components/frame/ToolShortcuts.vue';
import InvestigationsTabContent from './components/frame/InvestigationsTabContent.vue';

// Sidebar composable
const { sidebarHidden } = useSidebar();

// Analytics composable
const { trackPageView, trackEvent } = useAnalytics();

// Track page view and session start
onMounted(() => {
  trackPageView('/dashboard/vue');
  trackEvent('session_start', { timestamp: Date.now() });
});

// Track session end on component unmount
onBeforeUnmount(() => {
  trackEvent('session_end', { timestamp: Date.now() });
});
</script>

<style lang="scss" scoped>
/* Skip link for accessibility */
.skip-link {
  position: absolute;
  top: -40px;
  left: 0;
  background: var(--bs-primary);
  color: white;
  padding: 8px;
  text-decoration: none;
  z-index: 100;
}

.skip-link:focus {
  top: 0;
}

/* Wrapper for dark theme */
#wrapper {
  display: flex;
  min-height: 100vh;
  background-color: #1a1d21;
  color: #f8f9fa;
}

/* Main content area */
.content-area {
  flex: 1;
  margin-left: 272px; /* Sidebar width (240px) + padding (32px) */
  padding: 2rem;
  transition: margin-left 0.3s ease;
  background-color: #1a1d21;
  min-height: 100vh;
}

.content-area.expanded {
  margin-left: 32px; /* Collapsed sidebar width + padding */
}

/* Route transition styles (200ms fade as per AC-5.4) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
